import React, { useState, forwardRef } from 'react';
import { 
  ZoomIn, 
  ZoomOut, 
  Maximize2, 
  Download,
  RotateCw,
  FileText
} from 'lucide-react';
import PDFAnnotator from './PDFAnnotator';
import '../styles/BlueprintViewer.css';

interface BlueprintViewerProps {
  file: any;
  estimationId: string;
  activeMode?: 'draw' | 'specs' | 'rfi' | 'neca' | 'estimate';
  specsFiles?: any[];  // Array of all specs files
  currentSpecsFile?: any;  // Currently selected specs file
  onSpecsFileChange?: (fileId: string) => void;  // Callback to switch specs file
  drawingFiles?: any[];
  currentDrawingFile?: any;
  onDrawingChange?: (fileId: string) => void;
  currentDrawingFileId?: string;  // ID of current drawing for annotation tracking
  onScreenshotCaptured?: (base64Image: string) => void;
  electricalKey?: {
    components: Array<{
      name: string;
      description: string;
      color: string;
    }>;
  };
  onAutoAnnotationTrigger?: (config: {
    pageNumber: number;
    electricalKey: any;
    estimationId: string;
    onDismiss?: () => void;
  }) => void;
  onKeyExtracted?: (extractedKey: any) => void;
  onKeyExtractionProgress?: (progress: string) => void;
  autoExtractKeyOnSnapshot?: boolean;
  annotationCounts?: Map<string, Map<number, number>>;
  estimation?: any;
  isFilesPanelCollapsed?: boolean;
  onFilesToggle?: () => void;
  onHomeClick?: () => void;
  onModeChange?: (mode: 'draw' | 'specs' | 'rfi' | 'neca' | 'estimate') => void;
  isAgentChatCollapsed?: boolean;
  onChatToggle?: () => void;
  hasUnreadChat?: boolean;
  countingModeActive?: boolean;
  activePlacementComponent?: { name: string; symbol: string; color: string } | null;
  onPlacementComplete?: () => void;
  onStopCounting?: () => void;
  onAnnotationSaved?: (changedComponentTypes?: string[]) => Promise<void> | void;
  onWireRunsChanged?: () => void;
  onSwitchToCountingTab?: () => void;
  onSwitchToWiresTab?: () => void;
  onAddComponentToKey?: (component: { name: string; color: string; symbol: string }) => Promise<void>;
  agentChatRef?: React.RefObject<any>;
  pageIndex?: Array<{ page: number; label: string; sheetName?: string }> | null;
  onReindex?: () => Promise<void>;
  onLightingScheduleExtractionActiveChange?: (active: boolean) => void;
}

const BlueprintViewer = forwardRef<any, BlueprintViewerProps>(({ file, estimationId, activeMode = 'draw', specsFiles, currentSpecsFile, onSpecsFileChange, drawingFiles, currentDrawingFile, onDrawingChange, currentDrawingFileId, onScreenshotCaptured, electricalKey, onAutoAnnotationTrigger, onKeyExtracted, onKeyExtractionProgress, autoExtractKeyOnSnapshot, annotationCounts, estimation, isFilesPanelCollapsed, onFilesToggle, onHomeClick, onModeChange, isAgentChatCollapsed, onChatToggle, hasUnreadChat, countingModeActive, activePlacementComponent, onPlacementComplete, onStopCounting, onAnnotationSaved, onWireRunsChanged, onSwitchToCountingTab, onSwitchToWiresTab, onAddComponentToKey, agentChatRef, pageIndex, onReindex, onLightingScheduleExtractionActiveChange }, ref) => {
  const [zoom, setZoom] = useState(100);
  const [rotation, setRotation] = useState(0);
  
  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + 25, 300));
  };
  
  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev - 25, 25));
  };
  
  const handleFitToScreen = () => {
    setZoom(100);
    setRotation(0);
  };
  
  const handleRotate = () => {
    setRotation(prev => (prev + 90) % 360);
  };
  
  const handleDownload = () => {
    if (file?.url) {
      window.open(file.url, '_blank');
    }
  };
  
  // Render appropriate viewer based on mode
  const renderBlueprint = () => {
    // Always render PDFAnnotator - it handles the "no file" state with toolbar visible
    if (!file) {
      return <PDFAnnotator 
        ref={ref} 
        file={undefined} 
        estimationId={estimationId} 
        specsFiles={specsFiles}
        currentSpecsFile={currentSpecsFile}
        onSpecsFileChange={onSpecsFileChange}
        drawingFiles={drawingFiles}
        currentDrawingFile={currentDrawingFile}
        onDrawingFileChange={onDrawingChange}
        currentDrawingFileId={currentDrawingFileId}
        onScreenshotCaptured={onScreenshotCaptured} 
        electricalKey={electricalKey} 
        onAutoAnnotationTrigger={onAutoAnnotationTrigger} 
        onKeyExtracted={onKeyExtracted} 
        onKeyExtractionProgress={onKeyExtractionProgress} 
        autoExtractKeyOnSnapshot={autoExtractKeyOnSnapshot} 
        activeMode={activeMode}
        estimation={estimation}
        isFilesPanelCollapsed={isFilesPanelCollapsed}
        onFilesToggle={onFilesToggle}
        onHomeClick={onHomeClick}
        onModeChange={onModeChange}
        isAgentChatCollapsed={isAgentChatCollapsed}
        onChatToggle={onChatToggle}
        hasUnreadChat={hasUnreadChat}
        annotationCounts={annotationCounts}
        countingModeActive={countingModeActive}
        activePlacementComponent={activePlacementComponent}
        onPlacementComplete={onPlacementComplete}
        onStopCounting={onStopCounting}
        onAnnotationSaved={onAnnotationSaved}
        onWireRunsChanged={onWireRunsChanged}
        onSwitchToCountingTab={onSwitchToCountingTab}
        onSwitchToWiresTab={onSwitchToWiresTab}
        onAddComponentToKey={onAddComponentToKey}
        agentChatRef={agentChatRef}
        pageIndex={pageIndex}
        onReindex={onReindex}
        onLightingScheduleExtractionActiveChange={onLightingScheduleExtractionActiveChange}
      />;
    }
    
    const fileName = file.name || file.file_name || '';
    const fileExtension = fileName.split('.').pop()?.toLowerCase();
    const fileType = file.file_type || file.type || '';

    // Always use PDFAnnotator for PDFs - it handles both draw and specs modes
    // Check extension OR file type for PDF detection
    const isPDF = fileExtension === 'pdf' || fileType === 'application/pdf' || fileName.toLowerCase().includes('pdf');

    if (file.url && isPDF) {
      return <PDFAnnotator 
        ref={ref} 
        file={file} 
        estimationId={estimationId} 
        specsFiles={specsFiles}
        currentSpecsFile={currentSpecsFile}
        onSpecsFileChange={onSpecsFileChange}
        drawingFiles={drawingFiles}
        currentDrawingFile={currentDrawingFile}
        onDrawingFileChange={onDrawingChange}
        currentDrawingFileId={currentDrawingFileId}
        onScreenshotCaptured={onScreenshotCaptured} 
        electricalKey={electricalKey} 
        onAutoAnnotationTrigger={onAutoAnnotationTrigger} 
        onKeyExtracted={onKeyExtracted} 
        onKeyExtractionProgress={onKeyExtractionProgress} 
        autoExtractKeyOnSnapshot={autoExtractKeyOnSnapshot} 
        activeMode={activeMode}
        estimation={estimation}
        isFilesPanelCollapsed={isFilesPanelCollapsed}
        onFilesToggle={onFilesToggle}
        onHomeClick={onHomeClick}
        onModeChange={onModeChange}
        isAgentChatCollapsed={isAgentChatCollapsed}
        onChatToggle={onChatToggle}
        hasUnreadChat={hasUnreadChat}
        annotationCounts={annotationCounts}
        countingModeActive={countingModeActive}
        activePlacementComponent={activePlacementComponent}
        onPlacementComplete={onPlacementComplete}
        onStopCounting={onStopCounting}
        onAnnotationSaved={onAnnotationSaved}
        onWireRunsChanged={onWireRunsChanged}
        onSwitchToCountingTab={onSwitchToCountingTab}
        onSwitchToWiresTab={onSwitchToWiresTab}
        onAddComponentToKey={onAddComponentToKey}
        agentChatRef={agentChatRef}
        pageIndex={pageIndex}
        onReindex={onReindex}
        onLightingScheduleExtractionActiveChange={onLightingScheduleExtractionActiveChange}
      />;
    }
    
    // For images, show directly
    if (file.url && ['png', 'jpg', 'jpeg'].includes(fileExtension || '')) {
      return (
        <img
          src={file.url}
          alt={file.name}
          className="blueprint-image"
          style={{
            transform: `scale(${zoom / 100}) rotate(${rotation}deg)`,
          }}
        />
      );
    }
    
    return (
      <div className="blueprint-placeholder">
        <FileText size={48} />
        <p>Preview not available</p>
        <span>{file.name}</span>
      </div>
    );
  };
  
  const fileExtension = file?.name?.split('.').pop()?.toLowerCase();
  const isImage = file?.url && ['png', 'jpg', 'jpeg'].includes(fileExtension || '');

  return (
    <div className="blueprint-viewer">
      {/* Only show toolbar for images, PDFs have their own */}
      {isImage && (
        <div className="blueprint-toolbar">
          <div className="toolbar-group">
            <button 
              className="toolbar-btn" 
              onClick={handleZoomOut}
              title="Zoom Out"
            >
              <ZoomOut size={18} />
            </button>
            <span className="zoom-level">{zoom}%</span>
            <button 
              className="toolbar-btn" 
              onClick={handleZoomIn}
              title="Zoom In"
            >
              <ZoomIn size={18} />
            </button>
            <button 
              className="toolbar-btn" 
              onClick={handleFitToScreen}
              title="Fit to Screen"
            >
              <Maximize2 size={18} />
            </button>
          </div>
          
          <div className="toolbar-group">
            <button 
              className="toolbar-btn" 
              onClick={handleRotate}
              title="Rotate 90°"
            >
              <RotateCw size={18} />
            </button>
          </div>
          
          <div className="toolbar-group">
            <button 
              className="toolbar-btn" 
              onClick={handleDownload}
              title="Download"
            >
              <Download size={18} />
            </button>
          </div>
        </div>
      )}
      
      {/* Viewer Area */}
      <div className={`blueprint-viewport ${!isImage ? 'no-toolbar' : ''} ${activeMode === 'specs' ? 'specs-mode' : ''}`}>
        {renderBlueprint()}
      </div>
    </div>
  );
});

BlueprintViewer.displayName = 'BlueprintViewer';

export default BlueprintViewer;
